// FILE: stratify_website/src/layout/Meta.jsx
import { useEffect } from "react";
import { SITE } from "../config/site";

function upsertMeta(attrName, attrValue, content) {
  const selector =
    attrName === "property"
      ? `meta[property="${attrValue}"]`
      : `meta[name="${attrValue}"]`;

  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Meta({
  title,
  titleSuffix = "Stratify", // ✅ your desired brand suffix
  useSuffix = true,         // ✅ allow opting out if ever needed
  description,
  path = "/",
  keywords = [],
  ogImage = null,
  noindex = false,
  jsonLd = null,
}) {
  useEffect(() => {
    const safePath = (path || "/").startsWith("/") ? path : `/${path}`;
    const canonical = `${SITE.url}${safePath === "/" ? "" : safePath}`;

    // Title
    const fallbackTitle = SITE.brand?.logoText ?? SITE.name;
    const fullTitle = title
      ? (useSuffix ? `${title} | ${titleSuffix}` : title)
      : fallbackTitle;

    document.title = fullTitle;

    // Basic meta
    upsertMeta("name", "description", description || SITE.description);
    upsertMeta(
      "name",
      "keywords",
      (keywords?.length ? keywords : SITE.keywords).join(", ")
    );

    // Robots
    if (noindex) upsertMeta("name", "robots", "noindex, nofollow");
    else {
      const existing = document.head.querySelector(`meta[name="robots"]`);
      if (existing) existing.remove();
    }

    // Canonical
    upsertLink("canonical", canonical);

    // OpenGraph
    upsertMeta("property", "og:site_name", SITE.brand?.logoText ?? SITE.name);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || SITE.description);
    upsertMeta("property", "og:url", canonical);
    if (ogImage) upsertMeta("property", "og:image", ogImage);

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || SITE.description);
    if (ogImage) upsertMeta("name", "twitter:image", ogImage);

    // JSON-LD (replace any previous route JSON-LD)
    document
      .querySelectorAll(`script[data-seo-jsonld="true"]`)
      .forEach((n) => n.remove());

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-jsonld", "true");
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, titleSuffix, useSuffix, description, path, keywords, ogImage, noindex, jsonLd]);

  return null;
}
